import {
  Button,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { type Api, type Permission, admin, apis } from "../../data/api";
import { useAction, useLoad } from "../../data/data";
import { StatusBadge } from "../../ui/components/StatusBadge";
import {
  Empty,
  Failure,
  Field,
  FormRow,
  Hint,
  Loading,
  Mono,
  PageHeader,
  Section,
  Tag,
  Tags,
} from "../../ui/components/primitives";
import type { TenantPageProps } from "../pages";

// ---
// APIs
//
// Auth0 calls these APIs; the token calls the same thing `aud`, and the permissions on them are
// what land in `scope`. A permission only means something because some service checks it, which is
// what the enforcement badge is about.
// ---

export function Apis({ tenant, token, scopes }: TenantPageProps): React.JSX.Element {
  const list = useLoad(() => apis.list(tenant, token), [tenant, token]);
  const permissions = useLoad(() => admin.permissions(tenant, token), [tenant, token]);
  const action = useAction(() => {
    list.reload();
    permissions.reload();
  });
  const [api, setApi] = useState({ resource: "", profile: "data-plane" });
  const [perm, setPerm] = useState({ audience: "", key: "", description: "", machineOnly: false });

  const canManage = scopes.includes("permission:manage");
  const owned = (list.data ?? []).filter((each: Api) => each.ownerTenant === tenant);

  return (
    <>
      <PageHeader title="APIs" />

      {canManage && (
        <Section title="Register an API">
          <FormRow>
            <Field label="Identifier" sx={{ flex: "3 1 300px" }}>
              <TextField
                size="small"
                value={api.resource}
                onChange={(event) => setApi({ ...api, resource: event.target.value })}
                placeholder="https://api.acme.example/orders"
              />
            </Field>
            <Field label="Kind" sx={{ flex: "1 1 210px" }}>
              <Select
                size="small"
                value={api.profile}
                onChange={(event) => setApi({ ...api, profile: event.target.value })}
              >
                <MenuItem value="data-plane">Business API (names a project)</MenuItem>
                <MenuItem value="control-plane">Control plane (tenant only)</MenuItem>
              </Select>
            </Field>
            <Button
              variant="contained"
              disabled={api.resource === "" || action.busy}
              onClick={() =>
                action.run(async () => {
                  await apis.create(tenant, token, api);
                  setApi({ ...api, resource: "" });
                })
              }
            >
              Register
            </Button>
          </FormRow>
          <Hint>The identifier is a URI; it need not resolve.</Hint>
          <Failure error={action.error} />
        </Section>
      )}

      {canManage && owned.length > 0 && (
        <Section title="Add a permission">
          <FormRow>
            <Field label="API" sx={{ flex: "2 1 240px" }}>
              <Select
                size="small"
                value={perm.audience}
                onChange={(event) => setPerm({ ...perm, audience: event.target.value })}
              >
                {owned.map((each) => (
                  <MenuItem key={each.resource} value={each.resource}>
                    {each.resource}
                  </MenuItem>
                ))}
              </Select>
            </Field>
            <Field label="Permission" sx={{ flex: "1 1 160px" }}>
              <TextField
                size="small"
                value={perm.key}
                onChange={(event) => setPerm({ ...perm, key: event.target.value })}
                placeholder="order:read"
              />
            </Field>
            <Field label="Description" sx={{ flex: "2 1 200px" }}>
              <TextField
                size="small"
                value={perm.description}
                onChange={(event) => setPerm({ ...perm, description: event.target.value })}
              />
            </Field>
            <Button
              variant="contained"
              disabled={
                perm.audience === "" || perm.key === "" || perm.description === "" || action.busy
              }
              onClick={() =>
                action.run(async () => {
                  await apis.createPermission(tenant, token, perm);
                  setPerm({ ...perm, key: "", description: "" });
                })
              }
            >
              Add
            </Button>
          </FormRow>
          <Hint>
            <Mono>resource:action</Mono>, lower-kebab, no product prefix — the API already carries
            that boundary. New permissions start unenforced.
          </Hint>
        </Section>
      )}

      {list.pending ? (
        <Section>
          <Loading what="APIs" />
        </Section>
      ) : list.error !== null ? (
        <Section>
          <Failure error={list.error} />
        </Section>
      ) : (
        list.data?.length === 0 && (
          <Section>
            <Empty>No APIs yet. Register the first one above.</Empty>
          </Section>
        )
      )}

      {list.data?.map((each: Api) => {
        const mine = (permissions.data ?? []).filter(
          (permission: Permission) => permission.audience === each.resource,
        );
        return (
          <Section
            key={each.resource}
            flush
            title={<Mono sx={{ textTransform: "none", letterSpacing: 0 }}>{each.resource}</Mono>}
            action={
              <Tags>
                <Tag label={each.profile} />
                <Tag
                  label={each.ownerTenant === null ? "shared" : `owned by ${each.ownerTenant}`}
                />
              </Tags>
            }
          >
            {mine.length === 0 ? (
              <Empty>No permissions yet.</Empty>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Permission</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mine.map((permission) => (
                    <TableRow key={permission.key} hover>
                      <TableCell>
                        <Mono>{permission.key}</Mono>
                      </TableCell>
                      <TableCell>{permission.description}</TableCell>
                      <TableCell>
                        <Tags>
                          <StatusBadge
                            label={permission.enforcementStatus}
                            tone={
                              permission.enforcementStatus === "enforced" ? "success" : "warning"
                            }
                          />
                          {permission.machineOnly && (
                            <StatusBadge label="machine-only" tone="info" />
                          )}
                        </Tags>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>
        );
      })}
    </>
  );
}
